"use client";
import * as React from "react";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { Label } from "@/components/ui/label";
import { getCarServices } from "@/services/getCarServices";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/core/auth/contexts/AuthContext";
import { useForm } from "react-hook-form";
import {
  AddOrderSchema,
  AddOrderSchemaType,
  addOrderService,
} from "@/services/addOrderService";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { MultiSelect } from "@/components/ui/multi-select";
import { fetchEmployees } from "@/services/getEmployees";
import { getQueryParams } from "@/lib/getQueryParams";

export default function CreateOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const addOrderForm = useForm<AddOrderSchemaType>({
    resolver: zodResolver(AddOrderSchema),
    defaultValues: {
      service: "",
      customer: "",
      employee: "",
      plateNumber: getQueryParams(location).get("plateNumber"),
      company: user?.company?.name,
      addOns: [],
    },
  });

  const {
    data: carServices,
    isError: isCarServicesError,
    error: carServicesError,
  } = useQuery({
    queryKey: ["car-services"],
    queryFn: async () => getCarServices(),
    select: (data) => {
      return data.map((service: any) => ({
        ...service,
        companyName: service.company.name,
        value: service._id,
        label: service.name,
      }));
    },
  });

  const {
    data: employees,
    isError: isEmployeesError,
    error: employeesError,
  } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const data = await fetchEmployees();
      return data.map((employee: any) => {
        return { ...employee, company: employee.company?.name };
      });
    },
    enabled: !!user?.company?._id || user?.role === "super-admin",
  });

  const handleAddOrderMutation = useMutation({
    mutationFn: (formData: AddOrderSchemaType) => addOrderService(formData),
    onSuccess: () => {
      navigate("/app/orders");
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Create order successful");
    },
    onError: (error) => {
      const newError = error as any;
      toast.error(newError.response?.data?.message ?? "An error occured");
      console.error("An error occurred while creating order:", error);
    },
  });

  const { isPending } = handleAddOrderMutation;

  const onSubmit = (formData: AddOrderSchemaType) => {
    handleAddOrderMutation.mutate(formData);
  };

  const selectAddons = (value: any) => {
    addOrderForm.setValue("addOns", value);
  };

  return (
    <div className="w-full p-6 min-h-screen">
      <Link
        type="button"
        to="/app/orders"
        className={`${buttonVariants({ variant: "outline" })}`}
      >
        <ChevronLeft className="h-4 w-4" />
      </Link>
      <div className="mb-4 max-w-4xl mx-auto">
        <h2 className="text-xl my-4">Add Order</h2>
        <Form {...addOrderForm}>
          <form
            onSubmit={addOrderForm.handleSubmit(onSubmit)}
            className="border p-6  rounded-md"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
              {/* Service Dropdown */}
              <div className="w-full">
                <FormField
                  control={addOrderForm.control}
                  name="service"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Service</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a service" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectGroup>
                            {carServices
                              ?.filter(
                                (service) => service.category !== "Add-on"
                              )
                              .map((service: any) => (
                                <SelectItem
                                  key={service._id}
                                  value={service._id}
                                >
                                  {service.name}
                                </SelectItem>
                              ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      {isCarServicesError && (
                        <p className="text-sm text-destructive">
                          {carServicesError?.message ||
                            "Failed to load services"}
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Customer */}
              <div className="w-full">
                <FormField
                  control={addOrderForm.control}
                  name="customer"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Customer"
                          {...field}
                          disabled={true}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Employee */}
              <div className="w-full">
                {isEmployeesError && (
                  <p className="text-sm text-destructive">
                    {employeesError?.message || "Failed to load employees"}
                  </p>
                )}
                {employees && (
                  <FormField
                    control={addOrderForm.control}
                    name="employee"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employee</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Assign Employee" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectGroup>
                              {employees.map((employee: any) => (
                                <SelectItem
                                  key={employee._id}
                                  value={employee._id}
                                >
                                  {employee.name}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
              <div className="w-full">
                <FormField
                  control={addOrderForm.control}
                  name="plateNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Car Registration Number</FormLabel>
                      <FormControl>
                        <Input
                          disabled={true}
                          placeholder="Car Registration Number"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="w-full   flex items-center">
                {carServices && (
                  <FormField
                    control={addOrderForm.control}
                    name="addOns"
                    render={({ field }) => (
                      <FormItem>
                        {/* <FormLabel>Add Ons</FormLabel> */}
                        <FormControl>
                          <MultiSelect
                            title="Add Ons"
                            options={carServices.filter(
                              (service) => service.category === "Add-on"
                            )}
                            selectedValues={new Set(field.value)}
                            onSelectionChange={(selectedValues) => {
                              field.onChange(Array.from(selectedValues));
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
            <div className="">
              <Button
                type="submit"
                className="py-2 rounded-md"
                disabled={isPending}
              >
                {isPending ? "Creating order..." : "Create Order"}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
